// Write off one lot as expired stock (F-CTRL-N73). Names the lot, so what
// leaves the books is the stock that actually went in the bin — not whichever
// lot a general "issue" would have drawn first.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { intentScope, useIntentKey } from '@/lib/inventoryFlow/intentKey'

export interface WriteOffTarget {
  lotId: string
  itemName: string
  unit: string
  quantity: number
  unitCost?: number | null
  expiryDate?: string | null
  lotNo?: string | null
}

/** Every query a write-off makes stale. */
export const LOT_CHANGE_QUERY_KEYS = [
  'stockItems', 'stockItem', 'stockItemLots', 'stockSummary', 'stockAlerts', 'expiringBatches',
  'stockMovements', 'advancedStockReport', 'inventoryActivity', 'undatedLots',
] as const

export function WriteOffLotDialog({
  target,
  onClose,
  showToast,
}: {
  target: WriteOffTarget | null
  onClose: () => void
  showToast: (type: 'success' | 'error', message: string) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  // With the kitchen flow on, throwing stock away is a PIN-approved entry in
  // the waste log; the server asks for the manager PIN.
  const needsPin = useKitchenFlowEnabled()
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')
  const [pin, setPin] = useState('')

  useEffect(() => {
    if (!target) return
    setQty(String(target.quantity))
    setNote('')
    setPin('')
  }, [target])

  const qtyNum = parseFloat(qty)
  const qtyOk = Number.isFinite(qtyNum) && qtyNum > 0 && !!target && qtyNum <= target.quantity + 0.000001
  const value = qtyOk && target?.unitCost != null ? qtyNum * target.unitCost : null
  // One key per intent — this lot, this quantity, this note — reused on a
  // retry so a lost response never writes the stock off twice. The PIN is not
  // part of it: a wrong PIN stores nothing, so the corrected retry may reuse it.
  const intent = useIntentKey(intentScope('lot-write-off', target?.lotId ?? '', qty, note.trim()))

  const writeOff = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('No lot selected')
      return apiClient.writeOffStockLot(target.lotId, {
        quantity: qtyNum,
        note: note.trim() || undefined,
        ...(needsPin ? { pin: pin.trim() } : {}),
      }, intent.key)
    },
    onSuccess: (res) => {
      intent.renew()
      for (const key of LOT_CHANGE_QUERY_KEYS) qc.invalidateQueries({ queryKey: [key] })
      const written = res.data?.value
      showToast(
        'success',
        `Wrote off ${fmtQty(qtyNum)} ${target?.unit ?? ''} of ${target?.itemName ?? 'the item'}${written ? ` (${formatCurrency(written)})` : ''}. It shows under losses in Reports.`,
      )
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Could not write the stock off'),
  })

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !writeOff.isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Write off expired stock</DialogTitle>
          <DialogDescription>
            {target && (
              <>
                {target.itemName}
                {target.expiryDate ? `, expiry ${formatDateHuman(target.expiryDate)}` : ''}
                {target.lotNo ? `, lot ${target.lotNo}` : ''}. This takes the stock off your books and records it as
                waste. It cannot be undone.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label htmlFor="wo-qty">Quantity to write off{target ? ` (${target.unit})` : ''}</Label>
            <Input id="wo-qty" type="number" step="any" min="0" className="tabular-nums" value={qty} onChange={(e) => setQty(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              {target && !qtyOk && qty !== ''
                ? `Only ${fmtQty(target.quantity)} ${target.unit} is left in this lot.`
                : target
                  ? `${fmtQty(target.quantity)} ${target.unit} is left in this lot.${value != null ? ` Writing off ${formatCurrency(value)}.` : ''}`
                  : ''}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="wo-note">Note (optional)</Label>
            <Input id="wo-note" maxLength={500} placeholder="e.g. smelled off, pack swollen" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {needsPin && (
            <div className="space-y-2">
              <Label htmlFor="wo-pin">Manager PIN</Label>
              <Input id="wo-pin" type="password" inputMode="numeric" autoComplete="off" value={pin} onChange={(e) => setPin(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={writeOff.isPending}>Cancel</Button>
          <Button variant="destructive" disabled={!qtyOk || (needsPin && !pin.trim()) || writeOff.isPending} onClick={() => writeOff.mutate()}>
            {writeOff.isPending ? 'Writing off…' : 'Write off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
